import { displayToIso } from './nz-date';
import { parseTime } from './nz-time';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isNotBlank(value: string | null | undefined): boolean {
  if (!value) return false;
  return value.trim().length > 0;
}

export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  return EMAIL_RE.test(value.trim());
}

export function isValidPhone(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 12;
}

export function isValidDate(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && displayToIso(`${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`) !== null;
}

export function isValidTime(value: string | null | undefined): boolean {
  if (!value) return false;
  const result = parseTime(value);
  return result !== null && result !== '';
}

export function isPositiveNumber(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false;
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) && n > 0;
}

export function isPositiveInt(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false;
  const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  if (!Number.isFinite(n) || n < 1) return false;
  return Number.isInteger(typeof value === 'number' ? value : Number(value));
}

export function isInRange(value: string | number | null | undefined, min: number, max: number): boolean {
  if (value === null || value === undefined || value === '') return false;
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return false;
  return n >= min && n <= max;
}

export function withinMaxLength(value: string | null | undefined, max: number): boolean {
  if (!value) return true;
  return value.length <= max;
}

/**
 * Per-field error map. Absent key == valid field; a step/form is valid iff the
 * map is empty. The message stored under each key is the *exact* string the UI
 * renders, so the empty-vs-malformed distinction lives in the validator, not
 * the component. Shared by web (`inquiry/validators`) and admin
 * so public forms use one source of truth.
 */
export type FieldErrors<F extends string = string> = Partial<Record<F, string>>;

/**
 * A field rule: "required &/or must satisfy a predicate, with distinct messages
 * for empty vs malformed". Discriminated union — `invalidMsg` only exists
 * alongside a `predicate`, so a name-only (required-but-format-free) field
 * can't carry a dead invalidMsg.
 */
export type Rule =
  | { required?: boolean; emptyMsg?: string }
  | {
      required?: boolean;
      emptyMsg?: string;
      /** Format check; only runs on a non-empty value. */
      predicate: (v: string) => boolean;
      /** Shown when the value is non-empty and the predicate fails. */
      invalidMsg: string;
    };

/**
 * The single rule helper. Trims, then: empty → emptyMsg (only if required);
 * non-empty → invalidMsg if a predicate is present and fails; otherwise valid.
 * String-only by design — numeric fields that coerce empty away (e.g. a
 * clamp-to-0 input) keep their own positive-number path instead.
 */
export function checkField(
  value: string | null | undefined,
  rule: Rule,
): string | undefined {
  const v = (value ?? '').trim();
  if (v.length === 0) {
    return rule.required ? (rule.emptyMsg ?? 'This field is required') : undefined;
  }
  if ('predicate' in rule && !rule.predicate(v)) return rule.invalidMsg;
  return undefined;
}

/**
 * One field's spec for {@link collectFieldErrors}: the value to check and the
 * rule to check it against. The rule may be a plain {@link Rule} or any object
 * carrying at least the rule shape (e.g. a partial rule layered with extra
 * flags by a consumer), so the shared equipment rules compose here directly.
 */
export type FieldSpec = { value: string | null | undefined; rule: Rule };

/**
 * Run `checkField` over a record of `field → { value, rule }` and accumulate
 * the results into a {@link FieldErrors} map. This is the single home of the
 * "absent key ⇒ valid field, empty map ⇒ valid step" invariant: a field that
 * passes (`checkField` returns `undefined`) is *never* written into the map, so
 * callers can't accidentally store `undefined` under a key and silently break
 * the empty-map check. Keys are the typed field names; values are the exact
 * messages the UI renders.
 *
 * Per-field shape rules belong here; cross-field rules (date ordering,
 * conditional requireds keyed on another field) stay in the consumer, which can
 * mutate the returned map afterwards.
 */
export function collectFieldErrors<F extends string>(
  specs: Partial<Record<F, FieldSpec>>,
): FieldErrors<F> {
  const errors: FieldErrors<F> = {};
  for (const key of Object.keys(specs) as F[]) {
    const spec = specs[key];
    if (!spec) continue;
    const message = checkField(spec.value, spec.rule);
    if (message !== undefined) errors[key] = message;
  }
  return errors;
}

// ── Canonical contact (email/phone) validation ──────────────────────────────
//
// Email and phone are the one genuinely cross-consumer validation: the inquiry
// contact step (web) and the contractor/supplier modals (admin) both validate
// them with identical predicates AND identical user-facing messages. The
// predicate + message are paired here so a copy change propagates to both
// sides — neither consumer re-types the literal.
//
// These rules are format-only (the predicate runs only on a non-empty value).
// A consumer that requires the field layers `{ required: true, emptyMsg }` on
// top; a consumer where the field is optional uses the rule as-is.

export const EMAIL_INVALID_MSG = 'Enter a valid email address';
export const PHONE_INVALID_MSG = 'Enter a valid phone number (at least 8 digits)';

/** Format-only email rule: malformed non-empty value → EMAIL_INVALID_MSG. */
export const emailRule = {
  predicate: isValidEmail,
  invalidMsg: EMAIL_INVALID_MSG,
} as const;

/** Format-only phone rule: malformed non-empty value → PHONE_INVALID_MSG. */
export const phoneRule = {
  predicate: isValidPhone,
  invalidMsg: PHONE_INVALID_MSG,
} as const;
